import { useEffect, useState, useMemo } from "react";
import { api, API, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Btn, Badge, dt } from "../components/common";
import { toast } from "sonner";
import { useAuth } from "../auth";
import {
  ShieldCheck, Download, Trash2, Plus, HardDrive, CalendarClock,
  ShieldAlert, KeyRound, RefreshCw, Timer, ChevronDown, ChevronUp, Info,
  CheckCircle2, XCircle, ShieldQuestion, DatabaseBackup,
  Cloud, Server, Wrench, Zap, TestTube2, Edit3,
} from "lucide-react";

export default function DisasterRecovery() {
  const { can } = useAuth();
  const [tab, setTab] = useState("backups");
  const [backups, setBackups] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showSched, setShowSched] = useState(false);
  const [showRekey, setShowRekey] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [verify, setVerify] = useState(null); // {backup, result?, busy?, error?}
  const [restore, setRestore] = useState(null); // {backup, step, preview?, token?, result?, busy?, error?}
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    try {
      const [b, s, d] = await Promise.all([
        api.get("/dr/backups"),
        api.get("/dr/schedule"),
        api.get("/dr/destinations"),
      ]);
      setBackups(b.data || []);
      setSchedule(s.data || {});
      setDestinations(d.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { loadAll(); }, []);

  const totalSize = useMemo(() => backups.reduce((a, b) => a + (b.size_bytes || 0), 0), [backups]);
  const lastFull = backups.find((b) => b.type === "full");

  const doDelete = async (id) => {
    if (!window.confirm(`Apagar backup ${id}? Esta acao e irreversivel.`)) return;
    try {
      await api.delete(`/dr/backups/${id}`);
      toast.success("Backup apagado.");
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const download = async (b, source = "local") => {
    setBusy(true);
    try {
      const resp = await api.get(`/dr/backups/${b.id}/download`, {
        params: source && source !== "local" ? { source } : {},
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/octet-stream" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = b.filename || `${b.id}.tar.gz.enc`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!can("*")) {
    return (
      <div className="max-w-2xl">
        <PageHeader overline="Super Admin" title="Disaster Recovery" />
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 text-sm text-amber-700 flex items-start gap-2">
          <ShieldAlert size={16} className="shrink-0 mt-0.5" />
          <div>Esta area e restrita ao Super Admin.</div>
        </div>
      </div>
    );
  }

  const cols = [
    { key: "id", label: "Backup", render: (r) => (
      <div>
        <code className="text-xs font-mono text-navy">{r.id}</code>
        {r.note && <div className="text-[11px] text-slate-500 mt-0.5 italic">{r.note}</div>}
      </div>
    ) },
    { key: "type", label: "Tipo", render: (r) => (
      <div className="flex items-center gap-1.5">
        <Badge value={r.type} />
        {r.include_app && <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5" title="Inclui codigo aplicacional">+ app</span>}
      </div>
    ) },
    { key: "size_bytes", label: "Tamanho", render: (r) => fmtSize(r.size_bytes) },
    { key: "destinos", label: "Destinos", render: (r) => (
      <div className="flex flex-wrap gap-1">
        <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200 rounded px-1.5 py-0.5" title="Copia local em disco">
          <HardDrive size={10} /> local
        </span>
        {(r.remotes || []).map((rm, i) => (
          <span key={i} title={rm.error || `${rm.destination_name} - ${rm.remote_key || ""}`}
            className={`inline-flex items-center gap-1 text-[10px] font-medium rounded px-1.5 py-0.5 border ${rm.error ? "bg-red-50 text-red-700 border-red-200" : rm.tipo === "s3" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-blue/10 text-blue border-blue/30"}`}>
            {rm.tipo === "s3" ? <Cloud size={10} /> : <Server size={10} />} {rm.tipo}{rm.error ? "!" : ""}
          </span>
        ))}
      </div>
    ) },
    { key: "included_collections", label: "Collections", render: (r) => (
      <span className="text-[12px] text-slate-600">{(r.included_collections || []).length}</span>
    ) },
    { key: "created_by", label: "Criado por", render: (r) => (
      <div>
        <div className="text-[12px] text-navy">{r.created_by || "-"}</div>
        <div className="text-[11px] text-slate-500">{dt(r.created_at)}</div>
      </div>
    ) },
    { key: "sha256", label: "SHA-256", render: (r) => (
      <code className="text-[10px] font-mono text-slate-500" title={r.sha256}>{(r.sha256 || "").slice(0, 12)}...</code>
    ) },
    { key: "acoes", label: "", render: (r) => (
      <div className="flex gap-1">
        <button onClick={() => setVerify({ backup: r })} disabled={busy}
          data-testid={`dr-verify-${r.id}`}
          title="Testar restore (descifrar + validar integridade)"
          className="text-amber-600 hover:bg-amber-50 rounded p-1.5"><ShieldQuestion size={15} /></button>
        <button onClick={() => setRestore({ backup: r, step: "confirm" })} disabled={busy}
          data-testid={`dr-restore-${r.id}`}
          title="Restaurar automaticamente (dupla confirmacao)"
          className="text-red-600 hover:bg-red-50 rounded p-1.5"><DatabaseBackup size={15} /></button>
        <button onClick={() => download(r)} disabled={busy}
          data-testid={`dr-download-${r.id}`}
          title="Descarregar ficheiro cifrado"
          className="text-blue hover:bg-blue/10 rounded p-1.5"><Download size={15} /></button>
        <button onClick={() => doDelete(r.id)}
          data-testid={`dr-delete-${r.id}`}
          title="Apagar backup"
          className="text-red-600 hover:bg-red-50 rounded p-1.5"><Trash2 size={15} /></button>
      </div>
    ) },
  ];

  return (
    <div className="max-w-6xl">
      <PageHeader
        overline="Super Admin"
        title="Disaster Recovery"
        action={
          <div className="flex items-center gap-2">
            <Btn variant="ghost" onClick={() => setShowRekey(true)} testid="dr-rekey-btn">
              <KeyRound size={15} /> Rekey
            </Btn>
            <Btn variant="ghost" onClick={() => setShowSched(true)} testid="dr-schedule-btn">
              <CalendarClock size={15} /> Agendamento
            </Btn>
            <Btn variant="ghost" onClick={loadAll} testid="dr-reload-btn"><RefreshCw size={14} /></Btn>
            <Btn onClick={() => setShowNew(true)} testid="dr-new-btn"><Plus size={16} /> Novo backup</Btn>
          </div>
        }
      />

      <div className="border-b border-slate-200 flex text-[13px] mb-4">
        <button onClick={() => setTab("backups")} data-testid="dr-tab-backups"
          className={`py-2 px-3 font-medium ${tab === "backups" ? "text-blue border-b-2 border-blue" : "text-slate-500 hover:text-navy"}`}>Backups</button>
        <button onClick={() => setTab("destinos")} data-testid="dr-tab-destinos"
          className={`py-2 px-3 font-medium inline-flex items-center gap-1 ${tab === "destinos" ? "text-blue border-b-2 border-blue" : "text-slate-500 hover:text-navy"}`}>
          <Cloud size={13} /> Destinos remotos {destinations.length > 0 && <span className="text-[10px] bg-slate-100 border border-slate-200 rounded-full px-1.5">{destinations.length}</span>}
        </button>
      </div>

      {tab === "destinos" && (
        <DestinationsPanel destinations={destinations} onReload={loadAll} />
      )}

      {tab === "backups" && (<>
      {/* Cards de estado */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <StatCard icon={HardDrive} label="Backups guardados" value={backups.length} sub={fmtSize(totalSize)} />
        <StatCard icon={ShieldCheck} label="Ultimo full"
          value={lastFull ? dt(lastFull.created_at) : "-"}
          sub={lastFull ? lastFull.id : "sem backups"} />
        <StatCard icon={Timer} label="Agendamento"
          value={schedule?.enabled ? "Ativo" : "Inativo"}
          sub={schedule?.enabled ? `full ${schedule.full_hours}h · diff ${schedule.diff_hours}h` : "clique em Agendamento"}
          state={schedule?.enabled ? "ok" : "warn"} />
      </div>

      {/* Instrucoes gerais */}
      <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/60">
        <button onClick={() => setShowInstructions((v) => !v)} data-testid="dr-instructions-toggle"
          className="w-full flex items-center justify-between px-4 py-2.5 text-left">
          <div className="flex items-center gap-2 text-navy">
            <Info size={15} />
            <span className="font-semibold text-[13px]">Como restaurar noutro servidor</span>
          </div>
          {showInstructions ? <ChevronUp size={15} className="text-slate-400" /> : <ChevronDown size={15} className="text-slate-400" />}
        </button>
        {showInstructions && (
          <div className="px-4 pb-4 text-[12px] text-slate-700 space-y-2 leading-relaxed">
            <p>Cada backup e um <b>tarball cifrado</b> com AES-256-CBC + PBKDF2. A passphrase que definiu na altura da criacao <b>e obrigatoria</b> para descifrar. Guarde-a num gestor de segredos separado.</p>
            <ol className="list-decimal list-inside space-y-1 pl-1">
              <li>Descarregue o backup <code className="bg-white border border-slate-200 rounded px-1">.tar.gz.enc</code>.</li>
              <li>Descifre: <code className="block bg-white border border-slate-200 rounded p-2 mt-1 font-mono text-[11px]">openssl enc -d -aes-256-cbc -salt -pbkdf2 -iter 200000 -in FICHEIRO.tar.gz.enc -out FICHEIRO.tar.gz</code></li>
              <li>Extraia: <code className="bg-white border border-slate-200 rounded px-1">tar -xzf FICHEIRO.tar.gz</code></li>
              <li>Entre em <code className="bg-white border border-slate-200 rounded px-1">backup/</code> e leia <b>INSTRUCTIONS.md</b>.</li>
              <li>Corra <code className="bg-white border border-slate-200 rounded px-1">./restore.sh</code> - o script pergunta URI do Mongo, nome da BD e onde extrair o codigo.</li>
            </ol>
            <p><b>Diferenciais:</b> restaure primeiro o full de referencia, depois cada diff por ordem cronologica.</p>
          </div>
        )}
      </div>

      <div className="border border-slate-200 rounded-lg bg-white">
        <Table testid="dr-backups-table" columns={cols} rows={backups} />
      </div>
      </>)}

      {showNew && <NewBackupModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); loadAll(); }} lastFullId={lastFull?.id} destinations={destinations} />}
      {showSched && <ScheduleModal onClose={() => setShowSched(false)} onSaved={() => { setShowSched(false); loadAll(); }} current={schedule} />}
      {verify && <VerifyModal state={verify} setState={setVerify} onClose={() => setVerify(null)} />}
      {restore && <RestoreModal state={restore} setState={setRestore} onClose={() => { setRestore(null); loadAll(); }} />}
      {showRekey && <RekeyModal onClose={() => setShowRekey(false)} />}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, state }) {
  const cls = state === "ok" ? "border-emerald-200 bg-emerald-50/40" : state === "warn" ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-lg border ${cls} p-3`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
        <Icon size={13} className="text-slate-500" /> {label}
      </div>
      <div className="text-navy font-bold text-[16px] mt-1 truncate">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 truncate">{sub}</div>}
    </div>
  );
}

function NewBackupModal({ onClose, onCreated, lastFullId, destinations = [] }) {
  const [type, setType] = useState("full");
  const [includeApp, setIncludeApp] = useState(true);
  const [passphrase, setPassphrase] = useState("");
  const [passphrase2, setPassphrase2] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const activeDests = destinations.filter((d) => d.ativo);
  const [destChoice, setDestChoice] = useState(() => Object.fromEntries(activeDests.map((d) => [d.id, true])));

  const canDiff = !!lastFullId;
  const passOk = passphrase.length >= 12 && passphrase === passphrase2;

  const submit = async () => {
    if (type === "diff" && !canDiff) return toast.error("Precisa de pelo menos um backup full antes de fazer diff.");
    if (!passOk) return toast.error("A passphrase precisa de >=12 caracteres e ser igual nos dois campos.");
    setBusy(true);
    try {
      const destination_ids = activeDests.length > 0 ? activeDests.filter((d) => destChoice[d.id]).map((d) => d.id) : undefined;
      await api.post("/dr/backups", {
        type, include_app: includeApp && type === "full", passphrase, note,
        ...(destination_ids !== undefined ? { destination_ids } : {}),
      });
      toast.success(`Backup ${type} criado.`);
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="dr-new-modal">
      <div className="bg-white rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-blue text-[10px] uppercase tracking-[0.2em] font-semibold">Super Admin</div>
            <h2 className="text-navy font-bold text-lg">Novo backup</h2>
          </div>
          <button onClick={onClose} data-testid="dr-new-close" className="text-slate-400 hover:text-slate-700 text-xl leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[12px] font-semibold text-navy mb-1.5">Tipo de backup</label>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setType("full")} data-testid="dr-type-full"
                className={`text-left p-3 rounded-lg border ${type === "full" ? "border-blue bg-blue/5" : "border-slate-200 bg-white"}`}>
                <div className="text-navy font-semibold text-[13px]">Full (integral)</div>
                <div className="text-[11px] text-slate-500">Inclui base de dados, codigo, configuracoes e anexos.</div>
              </button>
              <button onClick={() => setType("diff")} data-testid="dr-type-diff" disabled={!canDiff}
                className={`text-left p-3 rounded-lg border ${type === "diff" ? "border-blue bg-blue/5" : "border-slate-200 bg-white"} ${!canDiff ? "opacity-50 cursor-not-allowed" : ""}`}>
                <div className="text-navy font-semibold text-[13px]">Diferencial</div>
                <div className="text-[11px] text-slate-500">{canDiff ? "So collections com checksum alterado desde o ultimo full." : "Requer um full previo."}</div>
              </button>
            </div>
          </div>

          {type === "full" && (
            <label className="flex items-center gap-2 text-[12px] text-navy cursor-pointer">
              <input type="checkbox" checked={includeApp} onChange={(e) => setIncludeApp(e.target.checked)}
                data-testid="dr-include-app" className="rounded border-slate-300" />
              Incluir codigo aplicacional (backend + frontend) e ficheiros .env
            </label>
          )}

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-800 flex items-start gap-2">
            <KeyRound size={14} className="mt-0.5 shrink-0" />
            <div>
              <b>Passphrase de cifra</b>. Sem ela o backup e <b>inutilizavel</b>. Guarde-a num gestor de segredos separado (nunca no mesmo local que o backup). Minimo 12 caracteres.
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Passphrase</span>
              <input type="password" value={passphrase} onChange={(e) => setPassphrase(e.target.value)}
                data-testid="dr-pass1" className="w-full px-2 py-1.5 border border-slate-300 rounded text-[12px] font-mono" />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Repetir</span>
              <input type="password" value={passphrase2} onChange={(e) => setPassphrase2(e.target.value)}
                data-testid="dr-pass2" className="w-full px-2 py-1.5 border border-slate-300 rounded text-[12px] font-mono" />
            </label>
          </div>

          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Nota (opcional)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Ex.: antes da migracao v2.3"
              data-testid="dr-note" className="w-full px-2 py-1.5 border border-slate-300 rounded text-[12px]" />
          </label>

          {activeDests.length > 0 && (
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-[12px] font-semibold text-navy mb-1.5 flex items-center gap-1">
                <Cloud size={12} /> Replicacao para destinos remotos
              </div>
              <div className="space-y-1">
                {activeDests.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 text-[12px] text-navy cursor-pointer">
                    <input type="checkbox" checked={!!destChoice[d.id]}
                      onChange={(e) => setDestChoice({ ...destChoice, [d.id]: e.target.checked })}
                      data-testid={`dr-new-dest-${d.id}`}
                      className="rounded border-slate-300" />
                    <span className={`inline-flex items-center gap-1 text-[10px] font-medium rounded px-1.5 py-0.5 border ${d.tipo === "s3" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-blue/10 text-blue border-blue/30"}`}>
                      {d.tipo === "s3" ? <Cloud size={10} /> : <Server size={10} />} {d.tipo}
                    </span>
                    {d.nome}
                  </label>
                ))}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Sera enviado em background apos o backup ser gravado localmente.</div>
            </div>
          )}
        </div>
        <div className="border-t border-slate-200 p-3 flex justify-end gap-2 bg-slate-50">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={submit} testid="dr-new-submit" disabled={busy || !passOk}>
            {busy ? "A criar backup..." : "Criar backup"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function ScheduleModal({ onClose, onSaved, current }) {
  const [enabled, setEnabled] = useState(!!current?.enabled);
  const [fullHours, setFullHours] = useState(current?.full_hours || 24);
  const [diffHours, setDiffHours] = useState(current?.diff_hours || 6);
  const [retainDays, setRetainDays] = useState(current?.retain_days || 30);
  const [passphrase, setPassphrase] = useState("");
  const [passphrase2, setPassphrase2] = useState("");
  const [busy, setBusy] = useState(false);

  const needsPassphrase = enabled && !current?.has_passphrase;
  const passOk = !needsPassphrase || (passphrase.length >= 12 && passphrase === passphrase2);

  const save = async () => {
    if (enabled) {
      const pass = passphrase || null;
      if (needsPassphrase && !pass) return toast.error("Defina uma passphrase para os backups automaticos.");
      if (pass && (pass.length < 12 || pass !== passphrase2)) return toast.error("Passphrase invalida ou nao coincide.");
    }
    setBusy(true);
    try {
      if (!enabled) {
        await api.post("/dr/schedule/disable");
      } else {
        await api.put("/dr/schedule", {
          enabled: true,
          full_hours: parseInt(fullHours, 10) || 24,
          diff_hours: parseInt(diffHours, 10) || 6,
          retain_days: parseInt(retainDays, 10) || 30,
          passphrase: passphrase || null,
        });
      }
      toast.success("Agendamento actualizado.");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="dr-schedule-modal">
      <div className="bg-white rounded-xl w-full max-w-xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-blue text-[10px] uppercase tracking-[0.2em] font-semibold">Super Admin</div>
            <h2 className="text-navy font-bold text-lg">Agendamento de backups</h2>
          </div>
          <button onClick={onClose} data-testid="dr-schedule-close" className="text-slate-400 hover:text-slate-700 text-xl leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-4">
          <label className="flex items-center gap-2 text-[13px] text-navy cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
              data-testid="dr-schedule-enable" className="rounded border-slate-300" />
            <b>Ativar agendamento automatico</b>
          </label>

          <div className="grid grid-cols-3 gap-3">
            <NumField label="Full a cada (h)" value={fullHours} onChange={setFullHours} disabled={!enabled} testid="dr-full-hours" />
            <NumField label="Diff a cada (h)" value={diffHours} onChange={setDiffHours} disabled={!enabled} testid="dr-diff-hours" />
            <NumField label="Retencao (dias)" value={retainDays} onChange={setRetainDays} disabled={!enabled} testid="dr-retain-days" />
          </div>

          <div className="text-[11px] text-slate-500 leading-relaxed">
            Um <b>full</b> extra e criado automaticamente todos os sabados as 23:59 UTC, alem do intervalo definido. Backups mais antigos que o periodo de retencao sao apagados. O agendador so corre se houver passphrase guardada.
          </div>

          {enabled && (
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
              <div className="text-[12px] font-semibold text-navy mb-2 flex items-center gap-1"><KeyRound size={12} /> Passphrase para backups automaticos</div>
              {current?.has_passphrase ? (
                <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 mb-2">
                  Existe uma passphrase guardada. Deixe os campos vazios para a manter, ou preencha para substituir.
                </div>
              ) : (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-2">
                  Ainda nao existe passphrase - defina uma para ativar o agendador.
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input type="password" value={passphrase} onChange={(e) => setPassphrase(e.target.value)}
                  placeholder="Nova passphrase (min. 12 caracteres)"
                  data-testid="dr-schedule-pass1" className="w-full px-2 py-1.5 border border-slate-300 rounded text-[12px] font-mono" />
                <input type="password" value={passphrase2} onChange={(e) => setPassphrase2(e.target.value)}
                  placeholder="Repetir passphrase"
                  data-testid="dr-schedule-pass2" className="w-full px-2 py-1.5 border border-slate-300 rounded text-[12px] font-mono" />
              </div>
            </div>
          )}

          {current?.last_full_at && (
            <div className="text-[11px] text-slate-500">Ultimo full automatico: {dt(current.last_full_at)}</div>
          )}
          {current?.last_diff_at && (
            <div className="text-[11px] text-slate-500">Ultimo diff automatico: {dt(current.last_diff_at)}</div>
          )}
          {current?.last_error && (
            <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="dr-schedule-last-error">
              Ultimo erro: {current.last_error}
            </div>
          )}
        </div>
        <div className="border-t border-slate-200 p-3 flex justify-end gap-2 bg-slate-50">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={save} testid="dr-schedule-save" disabled={busy || !passOk}>
            {busy ? "A guardar..." : "Guardar"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, disabled, testid }) {
  return (
    <label className={`block ${disabled ? "opacity-50" : ""}`}>
      <span className="block text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</span>
      <input type="number" min={1} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        data-testid={testid}
        className="w-full px-2 py-1.5 border border-slate-300 rounded text-[13px]" />
    </label>
  );
}

function fmtSize(bytes) {
  if (!bytes) return "-";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0; let v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

function VerifyModal({ state, setState, onClose }) {
  const { backup, result, error, busy } = state;
  const [passphrase, setPassphrase] = useState("");

  const run = async () => {
    if (!passphrase) return toast.error("Introduza a passphrase.");
    setState({ ...state, busy: true, error: null, result: null });
    try {
      const { data } = await api.post(`/dr/backups/${backup.id}/verify`, { passphrase });
      setState({ ...state, busy: false, result: data });
      if (data.ok) toast.success("Backup valido - passphrase correta e integridade OK.");
      else toast.error("Passphrase correta mas backup tem problemas estruturais.");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      setState({ ...state, busy: false, error: msg });
      toast.error(msg);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="dr-verify-modal">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-blue text-[10px] uppercase tracking-[0.2em] font-semibold">Super Admin</div>
            <h2 className="text-navy font-bold text-lg">Testar restore</h2>
            <div className="text-[11px] text-slate-500 mt-1">Descifra + valida integridade. Nao faz restore.</div>
          </div>
          <button onClick={onClose} data-testid="dr-verify-close" className="text-slate-400 hover:text-slate-700 text-xl leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Backup</div>
            <div className="text-navy font-mono text-[12px] break-all">{backup.id}</div>
            <div className="text-[11px] text-slate-500 mt-1">{backup.type} · {fmtSize(backup.size_bytes)} · criado {dt(backup.created_at)}</div>
          </div>

          {!result && !error && (
            <>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Passphrase</span>
                <input type="password" value={passphrase} onChange={(e) => setPassphrase(e.target.value)}
                  autoFocus data-testid="dr-verify-passphrase"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-[12px] font-mono" />
              </label>
              <div className="text-[11px] text-slate-500 leading-relaxed">
                Vamos descifrar o ficheiro para um diretorio temporario e validar a estrutura (metadata.json, restore.sh executavel, dumps de DB, snapshot da app, .env). O diretorio e apagado imediatamente apos a verificacao.
              </div>
            </>
          )}

          {busy && <div className="text-[13px] text-slate-600 flex items-center gap-2"><RefreshCw size={13} className="animate-spin" /> A descifrar e validar...</div>}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[12px] text-red-800 flex items-start gap-2" data-testid="dr-verify-error">
              <XCircle size={14} className="mt-0.5 shrink-0" />
              <div>{error}</div>
            </div>
          )}

          {result && (
            <div className="space-y-3" data-testid="dr-verify-result">
              <div className={`rounded-lg p-3 flex items-start gap-2 border ${result.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                {result.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <ShieldAlert size={14} className="mt-0.5 shrink-0" />}
                <div className="text-[12px]">
                  {result.ok
                    ? <>Backup <b>valido</b>. Passphrase correta e estrutura completa - este ficheiro pode ser restaurado noutro servidor.</>
                    : <>Passphrase correta, mas a estrutura tem problemas (ver checks abaixo). Considere criar um novo backup.</>}
                </div>
              </div>

              {result.metadata && (
                <div className="grid grid-cols-2 gap-2 text-[12px]">
                  <MetaRow label="Tipo" value={result.metadata.type} />
                  <MetaRow label="DB" value={result.metadata.db_name} />
                  <MetaRow label="Collections" value={result.metadata.collections_count} />
                  <MetaRow label="Incluidas" value={result.metadata.included_collections_count} />
                  <MetaRow label="Inclui codigo" value={result.metadata.include_app ? "sim" : "nao"} />
                  <MetaRow label="Base full" value={result.metadata.base_full_id || "-"} />
                </div>
              )}

              <div className="rounded border border-slate-200 divide-y divide-slate-200">
                {Object.entries(result.checks || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between px-3 py-1.5 text-[12px]" data-testid={`dr-verify-check-${k}`}>
                    <span className="text-slate-700">{k.replace(/_/g, " ")}</span>
                    <span className={typeof v === "boolean" ? (v ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold") : "text-navy font-mono"}>
                      {typeof v === "boolean" ? (v ? "OK" : "FALHA") : String(v)}
                    </span>
                  </div>
                ))}
              </div>

              {(result.db_files || []).length > 0 && (
                <div className="text-[11px] text-slate-500">
                  <div className="uppercase tracking-wider font-semibold mb-1">Ficheiros de DB no backup ({result.db_files.length})</div>
                  <div className="font-mono break-all">{result.db_files.join(", ")}</div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="border-t border-slate-200 p-3 flex justify-end gap-2 bg-slate-50">
          <Btn variant="ghost" onClick={onClose}>Fechar</Btn>
          {!result && !error && (
            <Btn onClick={run} testid="dr-verify-run" disabled={busy || !passphrase}>
              <ShieldCheck size={14} /> {busy ? "A validar..." : "Validar"}
            </Btn>
          )}
          {(result || error) && (
            <Btn variant="ghost" onClick={() => { setState({ ...state, result: null, error: null }); setPassphrase(""); }} testid="dr-verify-retry">
              Nova tentativa
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="border border-slate-200 rounded px-2 py-1 bg-white">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-navy font-mono text-[12px] truncate">{value ?? "-"}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Destinos remotos (S3, SFTP)
// ---------------------------------------------------------------------------

const EMPTY_S3 = { region: "eu-west-1", bucket: "", prefix: "saashub-backups/", endpoint_url: "", storage_class: "STANDARD" };
const EMPTY_SFTP = { host: "", port: 22, username: "", path: "/backups" };
const EMPTY_S3_CREDS = { access_key_id: "", secret_access_key: "", session_token: "" };
const EMPTY_SFTP_CREDS = { password: "", private_key: "", private_key_passphrase: "" };

function DestinationsPanel({ destinations, onReload }) {
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState({});

  const test = async (d) => {
    setTesting((t) => ({ ...t, [d.id]: "running" }));
    try {
      const { data } = await api.post(`/dr/destinations/${d.id}/test`);
      setTesting((t) => ({ ...t, [d.id]: { ok: true, ...data } }));
      toast.success(`Teste OK: ${d.nome}`);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      setTesting((t) => ({ ...t, [d.id]: { ok: false, error: msg } }));
      toast.error(msg);
    }
  };

  const del = async (d) => {
    if (!window.confirm(`Apagar destino '${d.nome}'? Backups ja replicados nao sao afetados na origem.`)) return;
    try {
      await api.delete(`/dr/destinations/${d.id}`);
      toast.success("Destino apagado.");
      onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="dr-destinations-panel">
      <div className="flex items-center justify-between">
        <div className="text-[13px] text-slate-600">Adicione destinos S3-compativel ou SFTP para replicar automaticamente cada backup. Credenciais cifradas em repouso.</div>
        <Btn onClick={() => setEditing({ tipo: "s3", nome: "", ativo: true, config: { ...EMPTY_S3 }, credentials: { ...EMPTY_S3_CREDS } })} testid="dr-dest-new">
          <Plus size={14} /> Novo destino
        </Btn>
      </div>

      {destinations.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-[13px] text-slate-500">
          Sem destinos configurados. Os backups continuam a ser guardados localmente em `/app/backups`.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {destinations.map((d) => (
          <div key={d.id} data-testid={`dr-dest-${d.id}`}
            className={`rounded-lg border p-3 bg-white ${d.ativo ? "border-slate-200" : "border-slate-200 opacity-70"}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-0.5 border ${d.tipo === "s3" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-blue/10 text-blue border-blue/30"}`}>
                    {d.tipo === "s3" ? <Cloud size={10} /> : <Server size={10} />} {d.tipo?.toUpperCase()}
                  </span>
                  {!d.ativo && <span className="text-[10px] bg-slate-100 text-slate-500 border border-slate-200 rounded px-1.5 py-0.5">inativo</span>}
                  {!d.has_credentials && <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">sem credenciais</span>}
                </div>
                <div className="font-semibold text-navy text-[13px] truncate">{d.nome}</div>
                <div className="text-[11px] text-slate-500 font-mono truncate">
                  {d.tipo === "s3" ? `${d.config?.bucket || "?"} · ${d.config?.region || "-"}${d.config?.endpoint_url ? " · custom endpoint" : ""}` : `${d.config?.username || "?"}@${d.config?.host || "?"}:${d.config?.port || 22}${d.config?.path || ""}`}
                </div>
                {d.config?.retain_days > 0 && (
                  <div className="text-[10px] text-slate-500 mt-0.5">Retencao propria: {d.config.retain_days}d</div>
                )}
                {d.stats && d.stats.count > 0 && (
                  <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`dr-dest-stats-${d.id}`}>
                    {d.stats.count} backups · {fmtSize(d.stats.total_size)}{d.stats.errors > 0 && <span className="text-red-600"> · {d.stats.errors} erro(s)</span>}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1 items-end">
                <button onClick={() => test(d)} data-testid={`dr-dest-test-${d.id}`}
                  className="text-[11px] inline-flex items-center gap-1 text-blue hover:underline">
                  <TestTube2 size={11} /> {testing[d.id] === "running" ? "..." : "Testar"}
                </button>
                <button onClick={() => setEditing(d)} data-testid={`dr-dest-edit-${d.id}`}
                  className="text-[11px] inline-flex items-center gap-1 text-slate-500 hover:text-navy">
                  <Edit3 size={11} /> Editar
                </button>
                <button onClick={() => del(d)} data-testid={`dr-dest-delete-${d.id}`}
                  className="text-[11px] inline-flex items-center gap-1 text-red-600 hover:underline">
                  <Trash2 size={11} /> Apagar
                </button>
              </div>
            </div>
            {testing[d.id] && testing[d.id] !== "running" && (
              <div className={`mt-2 text-[11px] rounded p-2 border ${testing[d.id].ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid={`dr-dest-test-result-${d.id}`}>
                {testing[d.id].ok ? "Ligacao OK." : testing[d.id].error}
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && <DestinationForm value={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onReload(); }} />}
    </div>
  );
}

function DestinationForm({ value, onClose, onSaved }) {
  const isNew = !value.id;
  const [tipo, setTipo] = useState(value.tipo || "s3");
  const [nome, setNome] = useState(value.nome || "");
  const [ativo, setAtivo] = useState(value.ativo !== false);
  const [config, setConfig] = useState(() => ({
    ...(tipo === "s3" ? EMPTY_S3 : EMPTY_SFTP),
    ...(value.config || {}),
  }));
  const [credentials, setCredentials] = useState(() => (tipo === "s3" ? { ...EMPTY_S3_CREDS } : { ...EMPTY_SFTP_CREDS }));
  const [busy, setBusy] = useState(false);

  const changeTipo = (t) => {
    setTipo(t);
    setConfig(t === "s3" ? { ...EMPTY_S3 } : { ...EMPTY_SFTP });
    setCredentials(t === "s3" ? { ...EMPTY_S3_CREDS } : { ...EMPTY_SFTP_CREDS });
  };

  const save = async () => {
    if (!nome) return toast.error("Nome e obrigatorio.");
    if (tipo === "s3" && !config.bucket) return toast.error("Bucket e obrigatorio.");
    if (tipo === "sftp" && (!config.host || !config.username)) return toast.error("Host e username sao obrigatorios.");
    // remover credenciais vazias para nao substituir as existentes com string vazia
    const cleanCreds = Object.fromEntries(Object.entries(credentials).filter(([, v]) => v && String(v).trim() !== ""));
    setBusy(true);
    try {
      const body = { nome, tipo, ativo, config, credentials: cleanCreds };
      if (isNew) await api.post("/dr/destinations", body);
      else await api.put(`/dr/destinations/${value.id}`, body);
      toast.success(isNew ? "Destino criado." : "Destino atualizado.");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="dr-dest-form">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-blue text-[10px] uppercase tracking-[0.2em] font-semibold">Super Admin</div>
            <h2 className="text-navy font-bold text-lg">{isNew ? "Novo destino remoto" : "Editar destino"}</h2>
          </div>
          <button onClick={onClose} data-testid="dr-dest-form-close" className="text-slate-400 hover:text-slate-700 text-xl leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <label className="block col-span-2">
              <span className="block text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Nome</span>
              <input value={nome} onChange={(e) => setNome(e.target.value)}
                data-testid="dr-dest-nome" placeholder="Ex.: AWS Produtos · SFTP OVH"
                className="w-full px-2 py-1.5 border border-slate-300 rounded text-[12px]" />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Tipo</span>
              <select value={tipo} onChange={(e) => changeTipo(e.target.value)} disabled={!isNew}
                data-testid="dr-dest-tipo"
                className="w-full px-2 py-1.5 border border-slate-300 rounded text-[12px] bg-white disabled:bg-slate-50">
                <option value="s3">S3 (AWS ou S3-compativel)</option>
                <option value="sftp">SFTP</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-[12px] text-navy cursor-pointer">
              <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)}
                data-testid="dr-dest-ativo" className="rounded border-slate-300" />
              Ativo (backups automaticos usam este destino)
            </label>
          </div>

          {tipo === "s3" ? (
            <>
              <div className="grid grid-cols-2 gap-2">
                <ConfigInput label="Bucket" value={config.bucket} testid="dr-dest-s3-bucket" onChange={(v) => setConfig({ ...config, bucket: v })} placeholder="saashub-backups" />
                <ConfigInput label="Region" value={config.region} testid="dr-dest-s3-region" onChange={(v) => setConfig({ ...config, region: v })} placeholder="eu-west-1" />
                <ConfigInput label="Prefix" value={config.prefix} testid="dr-dest-s3-prefix" onChange={(v) => setConfig({ ...config, prefix: v })} placeholder="saashub-backups/" />
                <ConfigInput label="Storage class" value={config.storage_class} testid="dr-dest-s3-class" onChange={(v) => setConfig({ ...config, storage_class: v })} placeholder="STANDARD | STANDARD_IA | GLACIER" />
                <ConfigInput label="Endpoint URL (opcional - S3-compativel)" value={config.endpoint_url} testid="dr-dest-s3-endpoint" onChange={(v) => setConfig({ ...config, endpoint_url: v })} placeholder="https://s3.eu-central.backblazeb2.com" span={2} />
                <ConfigInput label="Retencao no destino (dias - 0=global)" value={config.retain_days || 0} testid="dr-dest-s3-retain" onChange={(v) => setConfig({ ...config, retain_days: parseInt(v, 10) || 0 })} placeholder="30" />
              </div>
              <div className="border-t border-slate-200 pt-3">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1">
                  <KeyRound size={11} /> Credenciais {value.has_credentials && <span className="text-[10px] font-normal text-emerald-700 lowercase">(preenche so para substituir)</span>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <ConfigInput label="Access Key ID" value={credentials.access_key_id} testid="dr-dest-s3-akid" onChange={(v) => setCredentials({ ...credentials, access_key_id: v })} type="password" />
                  <ConfigInput label="Secret Access Key" value={credentials.secret_access_key} testid="dr-dest-s3-sak" onChange={(v) => setCredentials({ ...credentials, secret_access_key: v })} type="password" />
                  <ConfigInput label="Session Token (opcional)" value={credentials.session_token} testid="dr-dest-s3-st" onChange={(v) => setCredentials({ ...credentials, session_token: v })} type="password" span={2} />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <ConfigInput label="Host" value={config.host} testid="dr-dest-sftp-host" onChange={(v) => setConfig({ ...config, host: v })} placeholder="sftp.exemplo.pt" />
                <ConfigInput label="Port" value={config.port} testid="dr-dest-sftp-port" onChange={(v) => setConfig({ ...config, port: parseInt(v, 10) || 22 })} placeholder="22" />
                <ConfigInput label="Username" value={config.username} testid="dr-dest-sftp-user" onChange={(v) => setConfig({ ...config, username: v })} placeholder="backupuser" />
                <ConfigInput label="Path remoto" value={config.path} testid="dr-dest-sftp-path" onChange={(v) => setConfig({ ...config, path: v })} placeholder="/backups" />
                <ConfigInput label="Retencao no destino (dias - 0=global)" value={config.retain_days || 0} testid="dr-dest-sftp-retain" onChange={(v) => setConfig({ ...config, retain_days: parseInt(v, 10) || 0 })} placeholder="30" />
              </div>
              <div className="border-t border-slate-200 pt-3">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1">
                  <KeyRound size={11} /> Credenciais {value.has_credentials && <span className="text-[10px] font-normal text-emerald-700 lowercase">(preenche so para substituir)</span>}
                </div>
                <div className="grid grid-cols-1 gap-2">
                  <ConfigInput label="Password (deixe vazio se usar chave)" value={credentials.password} testid="dr-dest-sftp-pass" onChange={(v) => setCredentials({ ...credentials, password: v })} type="password" />
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Chave privada PEM (opcional)</span>
                    <textarea value={credentials.private_key} onChange={(e) => setCredentials({ ...credentials, private_key: e.target.value })}
                      data-testid="dr-dest-sftp-pk" rows={4}
                      placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                      className="w-full px-2 py-1.5 border border-slate-300 rounded text-[11px] font-mono" />
                  </label>
                  <ConfigInput label="Passphrase da chave (opcional)" value={credentials.private_key_passphrase} testid="dr-dest-sftp-pkpass" onChange={(v) => setCredentials({ ...credentials, private_key_passphrase: v })} type="password" />
                </div>
              </div>
            </>
          )}
        </div>
        <div className="border-t border-slate-200 p-3 flex justify-end gap-2 bg-slate-50">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={save} testid="dr-dest-save" disabled={busy}>
            {busy ? "A guardar..." : (isNew ? "Criar" : "Guardar")}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function ConfigInput({ label, value, onChange, placeholder, type = "text", span = 1, testid }) {
  return (
    <label className={`block ${span === 2 ? "col-span-2" : ""}`}>
      <span className="block text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</span>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        data-testid={testid}
        className="w-full px-2 py-1.5 border border-slate-300 rounded text-[12px] font-mono" />
    </label>
  );
}

function RekeyModal({ onClose }) {
  const [newSecret, setNewSecret] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const doPreview = async () => {
    if (newSecret.length < 16) return toast.error("A nova chave precisa de pelo menos 16 caracteres.");
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/dr/rekey", { new_secret: newSecret, dry_run: true });
      setPreview(data);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doExecute = async () => {
    if (confirmText !== "REKEY") return toast.error("Escreva 'REKEY' para confirmar.");
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/dr/rekey", { new_secret: newSecret, dry_run: false });
      setResult(data);
      toast.success("Rekey executado. Actualize JWT_SECRET no .env e reinicie o backend.");
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="dr-rekey-modal">
      <div className="bg-white rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-red-600 text-[10px] uppercase tracking-[0.2em] font-semibold">Zona critica</div>
            <h2 className="text-navy font-bold text-lg">Rotacao da chave (rekey)</h2>
            <div className="text-[11px] text-slate-500 mt-1">Decifra credenciais dos destinos e passphrase do scheduler com a chave actual e recifra-as com uma nova.</div>
          </div>
          <button onClick={onClose} data-testid="dr-rekey-close" className="text-slate-400 hover:text-slate-700 text-xl leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-800 flex items-start gap-2">
            <ShieldAlert size={14} className="mt-0.5 shrink-0" />
            <div>
              <b>Nota:</b> JWT_SECRET tambem assina os tokens JWT dos utilizadores. Depois de aplicar, <b>todos terao de fazer login</b> novamente. Guarde a nova chave num gestor de segredos antes de a substituir em <code>backend/.env</code>.
            </div>
          </div>
          {!result && (
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Nova chave (JWT_SECRET) - min. 16 caracteres</span>
              <input type="password" value={newSecret} onChange={(e) => setNewSecret(e.target.value)}
                data-testid="dr-rekey-new-secret" autoFocus
                className="w-full px-2 py-1.5 border border-slate-300 rounded text-[12px] font-mono" />
            </label>
          )}
          {preview && !result && (
            <div className="space-y-3" data-testid="dr-rekey-preview">
              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-[12px] text-navy space-y-1">
                <div>Vao ser recifradas <b>{preview.destination_fields}</b> credenciais em <b>{preview.destinations}</b> destino(s).</div>
                <div>Passphrase do scheduler: {preview.scheduler_passphrase ? <b className="text-emerald-700">sera recifrada</b> : <span className="text-slate-500 italic">nenhuma configurada</span>}.</div>
                {(preview.errors || []).length > 0 && (
                  <div className="text-red-700 mt-1">
                    <div className="font-semibold">Avisos:</div>
                    <ul className="list-disc pl-5">{preview.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                  </div>
                )}
              </div>
              <div className="text-[12px] text-slate-700">Escreva <code className="bg-slate-100 px-1 rounded">REKEY</code> para confirmar:</div>
              <input value={confirmText} onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                data-testid="dr-rekey-confirm-text"
                className="w-full px-2 py-1.5 border-2 border-red-300 rounded text-[13px] font-mono focus:outline-none focus:border-red-500" />
            </div>
          )}
          {result && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-[12px] text-emerald-800 space-y-2" data-testid="dr-rekey-result">
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0" /><div>
                Rekey executado: <b>{result.destination_fields}</b> campos em <b>{result.destinations}</b> destino(s) recifrados. Passphrase do scheduler: {result.scheduler_passphrase ? "OK" : "-"}.
              </div></div>
              {result.next_steps && (
                <div className="text-[11px] whitespace-pre-line bg-white/60 rounded p-2">{result.next_steps}</div>
              )}
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[12px] text-red-800 flex items-start gap-2" data-testid="dr-rekey-error">
              <XCircle size={14} className="mt-0.5 shrink-0" /><div>{error}</div>
            </div>
          )}
        </div>
        <div className="border-t border-slate-200 p-3 flex justify-end gap-2 bg-slate-50">
          <Btn variant="ghost" onClick={onClose}>{result ? "Fechar" : "Cancelar"}</Btn>
          {!preview && !result && (
            <Btn onClick={doPreview} testid="dr-rekey-preview-btn" disabled={busy || newSecret.length < 16}>
              {busy ? "A analisar..." : "Ver previa"}
            </Btn>
          )}
          {preview && !result && (
            <button onClick={doExecute} data-testid="dr-rekey-execute-btn"
              disabled={busy || confirmText !== "REKEY"}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold px-4 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-40 disabled:cursor-not-allowed">
              <KeyRound size={14} /> {busy ? "A executar..." : "Executar rekey"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function RestoreModal({ state, setState, onClose }) {
  const { backup } = state;
  const [passphrase, setPassphrase] = useState("");
  const [targetDb, setTargetDb] = useState("");
  const [dropExisting, setDropExisting] = useState(true);
  const [preview, setPreview] = useState(null);
  const [confirmToken, setConfirmToken] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [confirmText, setConfirmText] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!countdown) return;
    const t = setTimeout(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const step1 = async () => {
    if (!passphrase) return toast.error("Passphrase e obrigatoria.");
    setBusy(true); setError(null);
    try {
      const body = { passphrase, drop_existing: dropExisting };
      if (targetDb) body.target_db_name = targetDb;
      const { data } = await api.post(`/dr/backups/${backup.id}/restore`, body);
      if (data.step === "confirm") {
        setPreview(data.preview);
        setConfirmToken(data.confirm_token);
        setCountdown(data.expires_in_seconds || 90);
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const step2 = async () => {
    if (confirmText !== "RESTAURAR") return toast.error("Digite 'RESTAURAR' para confirmar.");
    setBusy(true); setError(null);
    try {
      const body = { passphrase, confirm_token: confirmToken, drop_existing: dropExisting };
      if (targetDb) body.target_db_name = targetDb;
      const { data } = await api.post(`/dr/backups/${backup.id}/restore`, body);
      setResult(data);
      if (data.ok) toast.success(`Restore concluido: ${data.restored_archives?.length || 0} archives restaurados.`);
      else toast.error(`Restore com ${data.errors?.length || 0} erros.`);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const inConfirm = !!preview && !result;
  const done = !!result;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="dr-restore-modal">
      <div className="bg-white rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-red-600 text-[10px] uppercase tracking-[0.2em] font-semibold">Zona critica</div>
            <h2 className="text-navy font-bold text-lg">Restaurar backup</h2>
            <div className="text-[11px] text-slate-500 mt-1">Sobrescreve a base de dados de destino. Dupla confirmacao obrigatoria.</div>
          </div>
          <button onClick={onClose} data-testid="dr-restore-close" className="text-slate-400 hover:text-slate-700 text-xl leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Backup</div>
            <div className="text-navy font-mono text-[12px] break-all">{backup.id}</div>
            <div className="text-[11px] text-slate-500 mt-1">{backup.type} · {fmtSize(backup.size_bytes)} · {dt(backup.created_at)}</div>
          </div>

          {!preview && !done && (
            <>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Passphrase</span>
                <input type="password" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} autoFocus
                  data-testid="dr-restore-passphrase"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-[12px] font-mono" />
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Nome da BD de destino (opcional - default = BD atual)</span>
                <input value={targetDb} onChange={(e) => setTargetDb(e.target.value)}
                  placeholder="Ex.: cognihub_staging"
                  data-testid="dr-restore-targetdb"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-[12px] font-mono" />
              </label>
              <label className="flex items-center gap-2 text-[12px] text-navy cursor-pointer">
                <input type="checkbox" checked={dropExisting} onChange={(e) => setDropExisting(e.target.checked)}
                  data-testid="dr-restore-drop" className="rounded border-slate-300" />
                Apagar collections existentes antes de restaurar (recomendado)
              </label>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-800 flex items-start gap-2">
                <ShieldAlert size={14} className="mt-0.5 shrink-0" />
                <div>Se restaurar sobre a BD em uso, considere fazer primeiro um backup atual. O restore aplica so a base de dados - o codigo fica intacto.</div>
              </div>
            </>
          )}

          {inConfirm && (
            <div className="space-y-3" data-testid="dr-restore-preview">
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2">
                <ShieldAlert size={14} className="text-red-600 mt-0.5 shrink-0" />
                <div className="text-[12px] text-red-800">
                  <b>Confirmacao final.</b> Vao ser restauradas {(preview.archives_to_restore || []).length} archives na BD <code className="bg-white px-1 rounded">{preview.target_db_name}</code>.
                  {preview.drop_existing && <> As collections destino serao <b>apagadas</b> antes.</>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[12px]">
                <MetaRow label="Tipo" value={preview.backup_type} />
                <MetaRow label="Source DB" value={preview.source_db_name} />
                <MetaRow label="Target DB" value={preview.target_db_name} />
                <MetaRow label="Drop" value={preview.drop_existing ? "sim" : "nao"} />
              </div>
              <div className="text-[11px] text-slate-500">
                <div className="uppercase tracking-wider font-semibold mb-1">Collections afetadas ({(preview.collections_affected || []).length})</div>
                <div className="font-mono break-all max-h-32 overflow-y-auto bg-slate-50 border border-slate-200 rounded p-2">
                  {(preview.collections_affected || []).join(", ") || "-"}
                </div>
              </div>
              <div className="text-[12px] text-slate-700">
                Token valido por <b>{countdown}s</b>. Escreva <code className="bg-slate-100 px-1 rounded">RESTAURAR</code> para confirmar:
              </div>
              <input value={confirmText} onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                data-testid="dr-restore-confirm-text" placeholder="RESTAURAR"
                className="w-full px-2 py-1.5 border-2 border-red-300 rounded text-[13px] font-mono focus:outline-none focus:border-red-500" />
            </div>
          )}

          {done && (
            <div className={`rounded-lg p-3 border flex items-start gap-2 ${result.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-800"}`} data-testid="dr-restore-result">
              {result.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <XCircle size={14} className="mt-0.5 shrink-0" />}
              <div className="text-[12px]">
                <div>{result.ok ? "Restore concluido com sucesso." : "Restore terminou com erros."}</div>
                <div className="text-[11px] mt-1">Target: <code className="bg-white/60 px-1 rounded">{result.target_db_name}</code></div>
                <div className="text-[11px]">Archives restaurados: {result.restored_archives?.length || 0}</div>
                {(result.errors || []).length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-[11px] underline">Ver {result.errors.length} erros</summary>
                    {result.errors.map((e, i) => (
                      <div key={i} className="text-[10px] font-mono mt-1 bg-white/60 rounded p-1">{e.archive}: {e.error.slice(0, 200)}</div>
                    ))}
                  </details>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[12px] text-red-800 flex items-start gap-2" data-testid="dr-restore-error">
              <XCircle size={14} className="mt-0.5 shrink-0" /><div>{error}</div>
            </div>
          )}
        </div>
        <div className="border-t border-slate-200 p-3 flex justify-end gap-2 bg-slate-50">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          {!preview && !done && (
            <Btn onClick={step1} testid="dr-restore-step1" disabled={busy || !passphrase}>
              {busy ? "A validar..." : "Ver previa do restore"}
            </Btn>
          )}
          {inConfirm && countdown > 0 && (
            <button onClick={step2} data-testid="dr-restore-step2"
              disabled={busy || confirmText !== "RESTAURAR"}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold px-4 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-40 disabled:cursor-not-allowed">
              <DatabaseBackup size={14} /> {busy ? "A restaurar..." : `Confirmar restore (${countdown}s)`}
            </button>
          )}
          {inConfirm && countdown === 0 && (
            <Btn variant="ghost" onClick={() => { setPreview(null); setConfirmToken(null); setConfirmText(""); }} testid="dr-restore-retry">
              Token expirou - recomecar
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}
